/**
 * ═══════════════════════════════════════════════════════════════
 * AUTH ERROR BOUNDARY - Planivio v1.0
 * ═══════════════════════════════════════════════════════════════
 * 
 * Löst das "invalid_grant" Token Refresh Problem!
 * 
 * Problem: Wenn Refresh Token abläuft, versucht Logto SDK endlos
 *          neue Tokens zu holen → Infinite Loop mit 795+ Fehlern
 * 
 * Lösung:  Diese Boundary fängt den Fehler ab und erzwingt Re-Login
 * 
 * @version 1.0.0
 */

import React, { Component } from 'react';
import { useAuth } from '../../hooks/useAuth';
// ✅ v1.1: REDIRECT_CONFIG für native Mobile OAuth (Capacitor)
import { REDIRECT_CONFIG } from '../../index';

// ═══════════════════════════════════════════════════════════════
// ERROR BOUNDARY CLASS (muss Class Component sein!)
// ═══════════════════════════════════════════════════════════════

class AuthErrorBoundaryClass extends Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('🔴 AuthErrorBoundary caught:', error);
    console.error('📍 Component Stack:', errorInfo.componentStack);
    
    // Zähle Fehler
    this.setState(prev => ({ errorCount: prev.errorCount + 1 }));
    
    // Check für Auth-Fehler
    const isAuthError = 
      error?.message?.includes('invalid_grant') ||
      error?.message?.includes('Not authenticated') ||
      error?.message?.includes('token') ||
      error?.name === 'LogtoClientError';
    
    if (isAuthError) {
      console.log('🔑 Auth-Fehler erkannt - Trigger Re-Login');
      this.props.onAuthError?.(error);
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <AuthErrorFallback 
          error={this.state.error}
          onRetry={() => this.setState({ hasError: false, error: null })}
        />
      );
    }

    return this.props.children;
  }
}

// ═══════════════════════════════════════════════════════════════
// FALLBACK UI
// ═══════════════════════════════════════════════════════════════

const AuthErrorFallback = ({ error, onRetry }) => {
  const { signIn, signOut } = useAuth();
  
  const isTokenError = 
    error?.message?.includes('invalid_grant') ||
    error?.message?.includes('token');

  const handleReLogin = async () => {
    try {
      // Erst ausloggen um alte Tokens zu löschen
      await signOut();
      // Dann neu einloggen - ✅ v1.1: REDIRECT_CONFIG für native Mobile OAuth
      setTimeout(() => {
        signIn({ redirectUri: REDIRECT_CONFIG.redirectUri });
      }, 100);
    } catch (e) {
      console.error('Re-Login Fehler:', e);
      // Fallback: Seite neu laden
      window.location.href = '/';
    }
  };

  const handleClearAndReload = () => {
    // Lösche alle Logto-Daten
    Object.keys(localStorage).forEach(key => {
      if (key.includes('logto') || key.includes('oidc')) {
        localStorage.removeItem(key);
      }
    });
    Object.keys(sessionStorage).forEach(key => {
      if (key.includes('logto') || key.includes('oidc')) {
        sessionStorage.removeItem(key);
      }
    });
    // Seite neu laden
    window.location.href = '/';
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.icon}>
          {isTokenError ? '🔑' : '⚠️'}
        </div>
        
        <h2 style={styles.title}>
          {isTokenError ? 'Sitzung abgelaufen' : 'Ein Fehler ist aufgetreten'}
        </h2>
        
        <p style={styles.message}>
          {isTokenError 
            ? 'Deine Anmeldesitzung ist abgelaufen. Bitte melde dich erneut an.'
            : 'Es gab ein Problem mit der Authentifizierung.'
          }
        </p>

        {/* Error Details (nur im Dev) */}
        {process.env.NODE_ENV === 'development' && (
          <details style={styles.details}>
            <summary style={styles.summary}>🔧 Technische Details</summary>
            <pre style={styles.errorCode}>
              {error?.message || 'Unbekannter Fehler'}
            </pre>
          </details>
        )}

        <div style={styles.buttons}>
          <button 
            style={styles.primaryButton}
            onClick={handleReLogin}
          >
            🔄 Neu anmelden
          </button>
          
          <button 
            style={styles.secondaryButton}
            onClick={handleClearAndReload}
          >
            🗑️ Cache leeren & neu laden
          </button>
          
          <button 
            style={styles.tertiaryButton}
            onClick={onRetry}
          >
            ↩️ Nochmal versuchen
          </button>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// WRAPPER MIT LOGTO HOOK
// ═══════════════════════════════════════════════════════════════

const AuthErrorBoundary = ({ children, fallback }) => {
  const { signOut, signIn } = useAuth();
  
  const handleAuthError = async (error) => {
    console.log('🔴 Auth Error Handler triggered:', error?.message);
    
    // Bei invalid_grant: Automatisch ausloggen
    if (error?.message?.includes('invalid_grant')) {
      try {
        await signOut();
        // Nach kurzer Pause neu einloggen - ✅ v1.1: REDIRECT_CONFIG für native Mobile OAuth
        setTimeout(() => {
          signIn({ redirectUri: REDIRECT_CONFIG.redirectUri });
        }, 500);
      } catch (e) {
        console.error('Auto-Logout fehlgeschlagen:', e);
      }
    }
  };

  return (
    <AuthErrorBoundaryClass 
      onAuthError={handleAuthError}
      fallback={fallback}
    >
      {children}
    </AuthErrorBoundaryClass>
  );
};

// ═══════════════════════════════════════════════════════════════
// STYLES
// ═══════════════════════════════════════════════════════════════

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
    padding: '20px'
  },
  card: {
    background: 'white',
    borderRadius: '20px',
    padding: '40px',
    maxWidth: '450px',
    width: '100%',
    textAlign: 'center',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)'
  },
  icon: {
    fontSize: '4rem',
    marginBottom: '20px'
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#1e293b',
    marginBottom: '12px'
  },
  message: {
    color: '#64748b',
    fontSize: '1rem',
    marginBottom: '24px',
    lineHeight: 1.6
  },
  details: {
    textAlign: 'left',
    marginBottom: '24px',
    background: '#fef2f2',
    borderRadius: '8px',
    padding: '12px'
  },
  summary: {
    cursor: 'pointer',
    fontWeight: 600,
    color: '#991b1b',
    fontSize: '0.875rem'
  },
  errorCode: {
    marginTop: '8px',
    fontSize: '0.75rem',
    color: '#dc2626',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all'
  },
  buttons: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  primaryButton: {
    padding: '14px 24px',
    background: 'linear-gradient(135deg, #4A90A4 0%, #6BA893 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 4px 15px rgba(74, 144, 164, 0.3)'
  },
  secondaryButton: {
    padding: '12px 24px',
    background: '#f1f5f9',
    color: '#475569',
    border: '2px solid #e2e8f0',
    borderRadius: '12px',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer'
  },
  tertiaryButton: {
    padding: '10px',
    background: 'transparent',
    color: '#94a3b8',
    border: 'none',
    fontSize: '0.85rem',
    cursor: 'pointer'
  }
};

export default AuthErrorBoundary;

